/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025-2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import Api, { type Company, type Software, URLS, type UserClient } from 'Api/Api';
import { AllowedRoles, useAuth } from 'Contexts/Auth';
import DOMPurify from 'dompurify';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { BsPencil, BsPlus, BsTrash, BsUpload } from 'react-icons/bs';
import Select from 'react-select';
import { getApiErrorMessage } from '../components/apiError';
import { FeedbackAlert, useFeedback } from '../components/Feedback';
import { SoftwareIcon } from '../components/UmatiIcons';
import styles from './Softwares.module.css';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const IMAGE_TYPES = ['logo', 'icon', 'screenshot'] as const;
type ImageType = (typeof IMAGE_TYPES)[number];

const isValidUrl = (value: string | null | undefined) => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const validateSoftwareId = (value: string, existingIds: string[]) => {
  const id = value.trim();
  if (!id) return 'Please enter a software ID.';
  if (/\s/.test(id)) return 'The software ID must not contain spaces.';
  if (existingIds.some((e) => e.toLowerCase() === id.toLowerCase())) return 'This software ID is already in use.';
  return null;
};

const sortByName = (list: Software[]) =>
  [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));

type SoftwareSidebarProps = {
  softwares: Software[];
  selectedId: number | undefined;
  setSelectedId: (id: number | undefined) => void;
  onAdd: () => void;
  canEdit: boolean;
};

const SoftwareSidebar = ({ softwares, selectedId, setSelectedId, onAdd, canEdit }: SoftwareSidebarProps) => (
  <>
    <header className={styles['header-row']}>
      <h2 className="header">
        <SoftwareIcon className="icon" />
        Software
      </h2>
    </header>
    <nav className={styles['softwares-overview']} aria-label="Software">
      <ul>
        {softwares.map((sw) => (
          <li key={sw.Id}>
            <button
              type="button"
              className={styles['software-button']}
              aria-current={selectedId === sw.Id}
              onClick={() => setSelectedId(sw.Id)}
            >
              {sw.name ?? sw.SoftwareId ?? String(sw.Id)}
            </button>
          </li>
        ))}
      </ul>
    </nav>
    {canEdit && (
      <Button size="sm" variant="outline-primary" className="w-100 mt-2" onClick={onAdd}>
        <BsPlus /> Add software
      </Button>
    )}
  </>
);

type SoftwareFormProps = {
  draft: Software;
  setDraft: React.Dispatch<React.SetStateAction<Software>>;
  companyOptions: { value: string; label: string }[];
  errors: Partial<Record<keyof Software, string>>;
  showErrors: boolean;
};

const SoftwareForm = ({ draft, setDraft, companyOptions, errors, showErrors }: SoftwareFormProps) => {
  const set = (field: keyof Software, value: string) => setDraft((d) => ({ ...d, [field]: value }));
  const invalid = (field: keyof Software) => showErrors && !!errors[field];
  const selectedCompany = companyOptions.find((o) => o.value === draft.RelatedCompany) ?? null;

  return (
    <>
      <Form.Group className="mb-3" controlId="sw-name">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={draft.name ?? ''}
          onChange={(e) => set('name', e.target.value)}
          isInvalid={invalid('name')}
        />
        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3" controlId="sw-software-id">
        <Form.Label>Software ID</Form.Label>
        <Form.Control
          value={draft.SoftwareId ?? ''}
          onChange={(e) => set('SoftwareId', e.target.value)}
          isInvalid={invalid('SoftwareId')}
        />
        <Form.Text muted>Unique identifier used by the dashboard, without spaces.</Form.Text>
        <Form.Control.Feedback type="invalid">{errors.SoftwareId}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3" controlId="sw-description">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={draft.description ?? ''}
          onChange={(e) => set('description', e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="sw-company">
        <Form.Label>Related company</Form.Label>
        <Select
          classNamePrefix="rs"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
          inputId="sw-company"
          isClearable
          placeholder="Select a company…"
          options={companyOptions}
          value={selectedCompany}
          onChange={(opt) => setDraft((d) => ({ ...d, RelatedCompany: opt?.value ?? EMPTY_GUID }))}
        />
      </Form.Group>

      <div className="row">
        <Form.Group className="col-md-7 mb-3" controlId="sw-manufacturer-url">
          <Form.Label>Manufacturer URL</Form.Label>
          <Form.Control
            value={draft.manufacturerURL ?? ''}
            onChange={(e) => set('manufacturerURL', e.target.value)}
            isInvalid={invalid('manufacturerURL')}
            placeholder="https://"
          />
          <Form.Control.Feedback type="invalid">{errors.manufacturerURL}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col-md-5 mb-3" controlId="sw-manufacturer-short">
          <Form.Label>Link text</Form.Label>
          <Form.Control
            value={draft.manufacturerShortURL ?? ''}
            onChange={(e) => set('manufacturerShortURL', e.target.value)}
            placeholder="e.g. manufacturer.com"
          />
        </Form.Group>
      </div>

      <div className="row">
        <Form.Group className="col-md-7 mb-3" controlId="sw-demo-url">
          <Form.Label>Demo page URL</Form.Label>
          <Form.Control
            value={draft.demoPageUrl ?? ''}
            onChange={(e) => set('demoPageUrl', e.target.value)}
            isInvalid={invalid('demoPageUrl')}
            placeholder="https://"
          />
          <Form.Control.Feedback type="invalid">{errors.demoPageUrl}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col-md-5 mb-3" controlId="sw-demo-short">
          <Form.Label>Link text</Form.Label>
          <Form.Control
            value={draft.demoPageShortUrl ?? ''}
            onChange={(e) => set('demoPageShortUrl', e.target.value)}
            placeholder="e.g. demo.manufacturer.com"
          />
        </Form.Group>
      </div>
    </>
  );
};

const validateSoftware = (draft: Software, otherIds: string[]): Partial<Record<keyof Software, string>> => {
  const errors: Partial<Record<keyof Software, string>> = {};
  if (!draft.name?.trim()) errors.name = 'Please enter a name.';
  const idError = validateSoftwareId(draft.SoftwareId ?? '', otherIds);
  if (idError) errors.SoftwareId = idError;
  if (!isValidUrl(draft.manufacturerURL))
    errors.manufacturerURL = 'Please enter a valid URL starting with http:// or https://.';
  if (!isValidUrl(draft.demoPageUrl))
    errors.demoPageUrl = 'Please enter a valid URL starting with http:// or https://.';
  return errors;
};

const normalize = (draft: Software): Software => ({
  ...draft,
  name: draft.name?.trim(),
  SoftwareId: draft.SoftwareId?.trim(),
  description: draft.description?.trim() || undefined,
  manufacturerURL: draft.manufacturerURL?.trim() || undefined,
  manufacturerShortURL: draft.manufacturerShortURL?.trim() || undefined,
  demoPageUrl: draft.demoPageUrl?.trim() || undefined,
  demoPageShortUrl: draft.demoPageShortUrl?.trim() || undefined,
});

type SoftwareDetailProps = {
  sw: Software;
  allSoftwares: Software[];
  allCompanies: Company[];
  allUsers: UserClient[];
  canEdit: boolean;
  onUpdate: (updated: Software) => void;
  onDelete: (id: number) => void;
};

const SoftwareDetail = ({
  sw,
  allSoftwares,
  allCompanies,
  allUsers,
  canEdit,
  onUpdate,
  onDelete,
}: SoftwareDetailProps) => {
  const [draft, setDraft] = useState<Software>({ ...sw });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [editServerError, setEditServerError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assignedUserIds, setAssignedUserIds] = useState<string[] | null>(null);
  const [savedUserIds, setSavedUserIds] = useState<string[]>([]);
  const fileInputs = useRef<Record<ImageType, HTMLInputElement | null>>({ logo: null, icon: null, screenshot: null });
  const { feedback, success, error } = useFeedback();

  useEffect(() => {
    setDraft({ ...sw });
  }, [sw]);

  useEffect(() => {
    if (!canEdit || sw.Id === undefined) return;
    Api.Software.getSoftwareUsers(sw.Id)
      .then(({ data }) => {
        const ids = data.map((u) => u.Id ?? '').filter(Boolean);
        setAssignedUserIds(ids);
        setSavedUserIds(ids);
      })
      .catch(() => {
        setAssignedUserIds([]);
        setSavedUserIds([]);
      });
  }, [sw.Id, canEdit]);

  const otherIds = allSoftwares.filter((s) => s.Id !== sw.Id).map((s) => s.SoftwareId ?? '');
  const errors = validateSoftware(draft, otherIds);
  const isValid = Object.keys(errors).length === 0;
  const isDirty = JSON.stringify(normalize(draft)) !== JSON.stringify(normalize(sw));

  const closeEdit = () => {
    setShowEditDialog(false);
    setShowErrors(false);
    setEditServerError(null);
    setDraft({ ...sw });
  };

  const handleSave = async () => {
    setShowErrors(true);
    setEditServerError(null);
    if (!isValid || sw.Id === undefined) return;
    const payload = normalize(draft);
    try {
      await Api.Software.putSoftware(sw.Id, payload);
      onUpdate(payload);
      closeEdit();
      success('Changes saved.');
    } catch (err) {
      setEditServerError(getApiErrorMessage(err, 'The changes could not be saved.'));
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    if (sw.Id === undefined) return;
    try {
      await Api.Software.deleteSoftware(sw.Id);
      onDelete(sw.Id);
    } catch (err) {
      error(err, 'The software could not be deleted.');
    }
  };

  const uploadImage = async (type: ImageType, file: File) => {
    if (sw.Id === undefined) return;
    try {
      const { data: updated } = await Api.Software.putSoftwareImage(sw.Id, type, file);
      onUpdate(updated);
      success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded.`);
    } catch (err) {
      error(err, `The ${type} could not be uploaded.`);
    }
  };

  const handleSaveUsers = async () => {
    if (sw.Id === undefined || assignedUserIds === null) return;
    try {
      await Api.Software.putSoftwareUsers(sw.Id, assignedUserIds);
      setSavedUserIds(assignedUserIds);
      success('Users updated.');
    } catch (err) {
      error(err, 'The users could not be saved.');
    }
  };

  const companyOptions = allCompanies.map((c) => ({ value: c.Id ?? '', label: c.Name ?? c.Id ?? '' }));
  const relatedCompany = allCompanies.find((c) => c.Id === sw.RelatedCompany);
  const userOptions = allUsers.map((u) => ({ value: u.Id ?? '', label: u.Username ?? u.Id ?? '' }));
  const usersChanged =
    assignedUserIds !== null &&
    JSON.stringify([...assignedUserIds].sort()) !== JSON.stringify([...savedUserIds].sort());

  const imageUrl = (type: ImageType) => {
    const name = type === 'logo' ? sw.logoUrl : type === 'icon' ? sw.iconUrl : sw.screenshotUrl;
    return name ? URLS.Software.getImage(name) : null;
  };

  return (
    <>
      <div className={styles['name-header']}>
        <h4 className="mb-0">{sw.name ?? sw.SoftwareId}</h4>
        {canEdit && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowEditDialog(true)}>
            <BsPencil className="me-1" />
            Edit
          </Button>
        )}
      </div>
      <p className={styles['name-subheading']}>Software ID: {sw.SoftwareId ?? '-'}</p>

      <FeedbackAlert feedback={feedback} />

      <Table bordered size="sm" className={styles.table}>
        <tbody>
          <tr>
            <th>Description</th>
            <td>
              {sw.description ? (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sw.description) }}
                />
              ) : (
                <span className="text-muted">No description</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Related company</th>
            <td>{relatedCompany?.Name ?? <span className="text-muted">Not related to a company</span>}</td>
          </tr>
          <tr>
            <th>Manufacturer</th>
            <td>
              {sw.manufacturerURL ? (
                <a href={sw.manufacturerURL} target="_blank" rel="noreferrer">
                  {sw.manufacturerShortURL || sw.manufacturerURL}
                </a>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Demo page</th>
            <td>
              {sw.demoPageUrl ? (
                <a href={sw.demoPageUrl} target="_blank" rel="noreferrer">
                  {sw.demoPageShortUrl || sw.demoPageUrl}
                </a>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
          </tr>
          {canEdit && (
            <tr>
              <th>Users with access</th>
              <td>
                <div className="d-flex gap-2 align-items-center">
                  <div style={{ flex: 1 }}>
                    <Select
                      classNamePrefix="rs"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
                      isMulti
                      isLoading={assignedUserIds === null}
                      placeholder="Select users…"
                      options={userOptions}
                      value={userOptions.filter((o) => assignedUserIds?.includes(o.value))}
                      onChange={(selected) => setAssignedUserIds(selected.map((s) => s.value))}
                    />
                  </div>
                  <Button size="sm" variant="primary" onClick={handleSaveUsers} disabled={!usersChanged}>
                    Save
                  </Button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <h6 className="mt-4 mb-2">Images</h6>
      <div className={styles['images-row']}>
        {IMAGE_TYPES.map((type) => {
          const url = imageUrl(type);
          return (
            <div key={type} className={styles['image-card']}>
              <span className={styles['image-label']}>{type}</span>
              {url ? (
                <img className={styles['sw-image']} src={url} alt={`${sw.name ?? ''} ${type}`} />
              ) : (
                <div className={styles['image-placeholder']}>No {type}</div>
              )}
              {canEdit && (
                <>
                  <Button size="sm" variant="outline-secondary" onClick={() => fileInputs.current[type]?.click()}>
                    <BsUpload className="me-1" />
                    {url ? 'Replace' : 'Upload'}
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={(el) => {
                      fileInputs.current[type] = el;
                    }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadImage(type, file);
                      e.target.value = '';
                    }}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="d-flex mt-4">
          <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => setShowDeleteConfirm(true)}>
            <BsTrash className="me-1" />
            Delete software
          </Button>
        </div>
      )}

      <Modal show={showEditDialog} onHide={closeEdit} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit software</Modal.Title>
        </Modal.Header>
        <Form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <Modal.Body>
            <SoftwareForm
              draft={draft}
              setDraft={setDraft}
              companyOptions={companyOptions}
              errors={errors}
              showErrors={showErrors}
            />
            {editServerError && <div className="alert alert-danger py-2 mb-0">{editServerError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEdit}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!isDirty}>
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete software</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you really want to delete <strong>{sw.name ?? sw.SoftwareId}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

type AddSoftwareModalProps = {
  show: boolean;
  allSoftwares: Software[];
  allCompanies: Company[];
  onClose: () => void;
  onCreated: (sw: Software) => void;
};

const emptySoftware = (): Software => ({ Id: 0, name: '', SoftwareId: '', RelatedCompany: EMPTY_GUID });

const AddSoftwareModal = ({ show, allSoftwares, allCompanies, onClose, onCreated }: AddSoftwareModalProps) => {
  const [draft, setDraft] = useState<Software>(emptySoftware);
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = validateSoftware(
    draft,
    allSoftwares.map((s) => s.SoftwareId ?? ''),
  );
  const isValid = Object.keys(errors).length === 0;

  const close = () => {
    setDraft(emptySoftware());
    setShowErrors(false);
    setServerError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    setServerError(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const { Id: _ignored, ...payload } = normalize(draft);
      const { data: created } = await Api.Software.postSoftware(payload as Software);
      onCreated(created);
      close();
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'The software could not be created.'));
    } finally {
      setSubmitting(false);
    }
  };

  const companyOptions = allCompanies.map((c) => ({ value: c.Id ?? '', label: c.Name ?? c.Id ?? '' }));

  return (
    <Modal show={show} onHide={close} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add software</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <SoftwareForm
            draft={draft}
            setDraft={setDraft}
            companyOptions={companyOptions}
            errors={errors}
            showErrors={showErrors}
          />
          <p className="text-muted small mb-0">Images can be uploaded after the software has been created.</p>
          {serverError && <div className="alert alert-danger py-2 mt-3 mb-0">{serverError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add software'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const SoftwarePage = () => {
  const auth = useAuth();
  const canEdit = auth.user?.Roles?.some((r) => r === 'SuperAdmin' || r === 'Admin') ?? false;

  const [softwares, setSoftwares] = useState<Software[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [allUsers, setAllUsers] = useState<UserClient[]>([]);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [showAdd, setShowAdd] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    Api.Software.getSoftwares()
      .then(({ data }) => {
        const sorted = sortByName(data);
        setSoftwares(sorted);
        if (sorted.length > 0) setSelectedId(sorted[0].Id);
      })
      .catch((err) => setLoadError(getApiErrorMessage(err, 'The software list could not be loaded.')));

    Api.Company.getCompanies()
      .then(({ data }) => setAllCompanies(data))
      .catch(() => setAllCompanies([]));
  }, []);

  useEffect(() => {
    if (!canEdit) return;
    Api.Users.getUsers()
      .then(({ data }) => setAllUsers(data))
      .catch(() => setAllUsers([]));
  }, [canEdit]);

  const selectedSw = softwares.find((s) => s.Id === selectedId);

  const handleUpdate = (updated: Software) => {
    setSoftwares((prev) => sortByName(prev.map((s) => (s.Id === updated.Id ? { ...s, ...updated } : s))));
  };

  const handleDelete = (id: number) => {
    setSoftwares((prev) => {
      const remaining = prev.filter((s) => s.Id !== id);
      setSelectedId((current) => (current === id ? remaining[0]?.Id : current));
      return remaining;
    });
  };

  const handleCreated = (created: Software) => {
    setSoftwares((prev) => sortByName([...prev, created]));
    setSelectedId(created.Id);
  };

  return (
    <AllowedRoles roles={['SuperAdmin', 'Admin', 'CompanyMember']}>
      <div className={styles['softwares-page']}>
        <aside className={styles['softwares-sidebar']}>
          <SoftwareSidebar
            softwares={softwares}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            onAdd={() => setShowAdd(true)}
            canEdit={canEdit}
          />
        </aside>

        <main className={styles['softwares-content']}>
          {loadError && <div className="alert alert-danger">{loadError}</div>}

          {!loadError && softwares.length === 0 && (
            <div className="text-muted">
              <h5>{canEdit ? 'No software yet' : 'No software available'}</h5>
              <p>
                {canEdit ? 'Use "Add software" to create the first entry.' : 'There is no software for your companies.'}
              </p>
            </div>
          )}

          {selectedSw && (
            <SoftwareDetail
              key={selectedSw.Id}
              sw={selectedSw}
              allSoftwares={softwares}
              allCompanies={allCompanies}
              allUsers={allUsers}
              canEdit={canEdit}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          )}
        </main>
      </div>

      <AddSoftwareModal
        show={showAdd && canEdit}
        allSoftwares={softwares}
        allCompanies={allCompanies}
        onClose={() => setShowAdd(false)}
        onCreated={handleCreated}
      />
    </AllowedRoles>
  );
};

export default SoftwarePage;
